import { useState, useEffect, useMemo } from 'react';
import { useImpersonation } from '@center-technologies/shared-api-clients';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import { ProvidedContext, ApproachReference } from 'types/approach';

export interface ResolvedContext {
  contextSystemName: string;
  displayName: string;
  description: string | null;
  icon: string | null;
}

export interface UseResolvedContextsResult {
  contexts: ResolvedContext[];
  loading: boolean;
}

function formatSystemName(systemName: string): string {
  return systemName
    .replace(/^center_/, '')
    .replace(/_context$/, '')
    .replaceAll('_', ' ')
    .replaceAll(/\b\w/g, (c) => c.toUpperCase());
}

function getLocalizedValue(
  field: Record<string, string> | string | null | undefined,
  locale = 'en-US'
): string | null {
  if (!field) return null;
  if (typeof field === 'string') return field;
  return field[locale] ?? field['en-US'] ?? Object.values(field)[0] ?? null;
}

/**
 * Resolves provided contexts to display metadata. For composite approaches
 * where providedContexts is empty but subApproaches exist, fetches each
 * sub-approach definition and unions their providedContexts.
 */
export function useResolvedContexts(
  providedContexts: ProvidedContext[] | undefined | null,
  subApproaches?: ApproachReference[] | undefined | null
): UseResolvedContextsResult {
  const apiClients = useApiClientsOrNull();
  const { impersonationDetails } = useImpersonation();
  const organizationId = impersonationDetails?.organizationId;

  const hasDirectContexts = !!providedContexts && providedContexts.length > 0;
  const hasSubApproaches = !!subApproaches && subApproaches.length > 0;

  // Stabilize dependency: serialize system names so array reference changes don't retrigger
  const contextsKey = useMemo(
    () =>
      providedContexts
        ?.map((pc) => pc.contextSystemName)
        .sort()
        .join(',') ?? '',
    [providedContexts]
  );

  const subApproachesKey = useMemo(
    () =>
      subApproaches
        ?.map((sa) => sa.approachSystemName)
        .sort()
        .join(',') ?? '',
    [subApproaches]
  );

  const [contexts, setContexts] = useState<ResolvedContext[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!hasDirectContexts && !hasSubApproaches) {
      setContexts([]);
      setLoading(false);
      return;
    }

    if (!apiClients?.schemaClient || !organizationId) {
      return;
    }

    let cancelled = false;

    const resolve = async () => {
      setLoading(true);

      const effectiveContexts: ProvidedContext[] = providedContexts ? [...providedContexts] : [];

      // For composite approaches: fetch sub-approach definitions to get their contexts
      if (!hasDirectContexts && hasSubApproaches && subApproaches) {
        // The sub-approach definitions are independent reads; Promise.all keeps the declared order.
        const included = subApproaches.filter((sa) => sa.mode === 'include');
        const providedBySubApproach = await Promise.all(
          included.map(async (sa): Promise<ProvidedContext[]> => {
            try {
              const response = await apiClients.schemaClient.get(
                `/approaches/${encodeURIComponent(sa.approachSystemName)}`,
                { params: { includeMetrics: false } }
              );
              const subDef = response?.approach?.latestVersion?.definition ??
                response?.approach?.version?.definition ??
                response?.data?.approach?.latestVersion?.definition ??
                response?.data?.approach?.version?.definition;
              return subDef?.providedContexts ?? [];
            } catch {
              // Sub-approach fetch failed — skip
              return [];
            }
          })
        );
        for (const provided of providedBySubApproach) {
          effectiveContexts.push(...provided);
        }
      }

      if (effectiveContexts.length === 0) {
        if (!cancelled) {
          setContexts([]);
          setLoading(false);
        }
        return;
      }

      // Deduplicate by contextSystemName
      const uniqueNames = new Set<string>();
      const deduped: ProvidedContext[] = [];
      for (const pc of effectiveContexts) {
        if (!uniqueNames.has(pc.contextSystemName)) {
          uniqueNames.add(pc.contextSystemName);
          deduped.push(pc);
        }
      }

      // Each context is an independent read; Promise.all keeps the deduplicated order.
      const results: ResolvedContext[] = await Promise.all(
        deduped.map(async (pc): Promise<ResolvedContext> => {
          const fallback: ResolvedContext = {
            contextSystemName: pc.contextSystemName,
            displayName: formatSystemName(pc.contextSystemName),
            description: null,
            icon: null,
          };
          try {
            const response = await apiClients.schemaClient.get(
              `/contexts/system_name/${encodeURIComponent(pc.contextSystemName)}`
            );

            const contextData = response?.data ?? response;

            const displayName = getLocalizedValue(contextData?.name);
            if (!displayName) return fallback;

            return {
              contextSystemName: pc.contextSystemName,
              displayName,
              description: getLocalizedValue(contextData?.description),
              icon: contextData?.icon ?? null,
            };
          } catch {
            return fallback;
          }
        })
      );

      if (!cancelled) {
        setContexts(results);
        setLoading(false);
      }
    };

    resolve();

    return () => {
      cancelled = true;
    };
  }, [contextsKey, subApproachesKey, hasDirectContexts, hasSubApproaches, apiClients, organizationId]);

  return { contexts, loading };
}
